import { useTranslation } from "react-i18next"

import type { RewardCategory } from "@/components/coins/reward-card"
import { cn } from "@/lib/utils"

/**
 * OKS-556 — §19 category chip bar (UX Spec §2.3). A labelled, keyboard-
 * navigable control group that filters the catalogue to one category —
 * `All · Gear · Experiences · Perks`. Selection is CLIENT-SIDE (TRD KD-8, no
 * URL param): the parent narrows the grouped sections to the active filter.
 *
 * `all` is the reset chip. Only categories the brand actually has rewards in
 * are offered (empty categories are hidden — UX §3 edge), so tapping a chip
 * never lands on an empty section.
 *
 * Accessibility: rendered as a `role="tablist"` of `role="tab"` chips; the
 * selected chip exposes `aria-selected` so AT announces the active filter
 * (affordance is not colour-only). Chips scroll horizontally on narrow
 * viewports (CSS overflow — no JS).
 */

export type CategoryFilter = "all" | RewardCategory

interface CategoryChipBarProps {
  /** Categories present in the catalogue, in display order. `all` is prepended. */
  available: RewardCategory[]
  selected: CategoryFilter
  onSelect: (filter: CategoryFilter) => void
  className?: string
}

const CATEGORY_ORDER: RewardCategory[] = ["gear", "experiences", "perks"]

export function CategoryChipBar({
  available,
  selected,
  onSelect,
  className,
}: CategoryChipBarProps) {
  const { t } = useTranslation()

  // Keep a stable display order regardless of the input array's order.
  const ordered = CATEGORY_ORDER.filter((c) => available.includes(c))
  const filters: CategoryFilter[] = ["all", ...ordered]

  return (
    <div
      role="tablist"
      aria-label={t("rewards.creator.categories.aria_label")}
      data-testid="category-chip-bar"
      className={cn(
        "-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      )}
    >
      {filters.map((filter) => {
        const isSelected = filter === selected
        return (
          <button
            key={filter}
            type="button"
            role="tab"
            aria-selected={isSelected}
            data-testid={`category-chip-${filter}`}
            data-selected={isSelected}
            onClick={() => onSelect(filter)}
            className={cn(
              "focus-visible:ring-ring shrink-0 rounded-full border px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:outline-none",
              isSelected
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-card text-muted-foreground hover:text-foreground",
            )}
          >
            {filter === "all"
              ? t("rewards.creator.categories.all")
              : t(`rewards.creator.categories.${filter}`)}
          </button>
        )
      })}
    </div>
  )
}
